import { createElement, type ComponentProps } from 'react';

import { AccordionItem, AccordionItemContent, chakra } from '@plurall/elo-v3';

import { clsx } from '@/lib/formatters/classNames';

import {
  navAccordionContentRecipe,
  navAccordionIndicatorRecipe,
  navAccordionItemRecipe,
  navAccordionTriggerRecipe,
  navSubitemRecipe,
  navSublistRecipe,
} from './local-navigation-submenu-recipe';

const BaseNavAccordionItemFactory = chakra(AccordionItem, navAccordionItemRecipe);
const BaseNavAccordionContentFactory = chakra(AccordionItemContent, navAccordionContentRecipe);
const BaseNavAccordionIndicatorFactory = chakra('span', navAccordionIndicatorRecipe);
const BaseNavSublistFactory = chakra('ul', navSublistRecipe);
const BaseNavSubitemFactory = chakra('li', navSubitemRecipe);

const BaseNavAccordionTriggerFactory = chakra('button', navAccordionTriggerRecipe);

export type NavAccordionItemFactoryProps = ComponentProps<typeof BaseNavAccordionItemFactory>;
export type NavAccordionContentFactoryProps = ComponentProps<typeof BaseNavAccordionContentFactory>;
export type NavAccordionIndicatorFactoryProps = ComponentProps<typeof BaseNavAccordionIndicatorFactory>;
export type NavSublistFactoryProps = ComponentProps<typeof BaseNavSublistFactory>;
export type NavSubitemFactoryProps = ComponentProps<typeof BaseNavSubitemFactory>;

export type NavAccordionTriggerFactoryProps = ComponentProps<typeof BaseNavAccordionTriggerFactory> & {
  isActive?: boolean;
};

export function NavAccordionItemFactory({ className, ...props }: NavAccordionItemFactoryProps) {
  return createElement(BaseNavAccordionItemFactory, {
    ...props,
    className: clsx(className, 'local-navigation__accordion-item'),
  });
}

export function NavAccordionContentFactory({ className, ...props }: NavAccordionContentFactoryProps) {
  return createElement(BaseNavAccordionContentFactory, {
    ...props,
    className: clsx(className, 'local-navigation__accordion-panel'),
  });
}

export function NavAccordionIndicatorFactory({ className, ...props }: NavAccordionIndicatorFactoryProps) {
  return createElement(BaseNavAccordionIndicatorFactory, {
    ...props,
    className: clsx(className, 'local-navigation__accordion-indicator'),
  });
}

export function NavSublistFactory({ className, ...props }: NavSublistFactoryProps) {
  return createElement(BaseNavSublistFactory, {
    ...props,
    className: clsx(className, 'local-navigation__sublist'),
  });
}

export function NavSubitemFactory({ className, ...props }: NavSubitemFactoryProps) {
  return createElement(BaseNavSubitemFactory, {
    ...props,
    className: clsx(className, 'local-navigation__subitem'),
  });
}

export function NavAccordionTriggerFactory({ isActive = false, className, ...props }: NavAccordionTriggerFactoryProps) {
  return createElement(BaseNavAccordionTriggerFactory, {
    ...props,
    isActive,
    className: clsx(
      className,
      'local-navigation__accordion-trigger',
      isActive && 'local-navigation__accordion-trigger--active'
    ),
  });
}
